import type { ImportDraft, Transaction } from '@finapp/types';
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { apiUrl, fetchWithAuth } from '../config/api';
import type { TransactionListFilter } from '../services/transactionsApi';
import { transactionsApi } from '../services/transactionsApi';

interface UseTransactionDataResult {
  transactions: Transaction[];
  drafts: ImportDraft[];
  userRole: string;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
  setTransactions: React.Dispatch<React.SetStateAction<Transaction[]>>;
  total: number;
  totalPages: number;
}

/**
 * Loads a page of transactions (via transactionsApi.ts) plus import drafts/user role. Follows
 * the manual fetch-hook convention used elsewhere in this codebase (useState/useEffect, no
 * caching/revalidation - see useForecastData.ts) - the filter is owned by the caller (the
 * Transactions page), same as useForecastData's year/asOfDate/periodType params, so changing
 * page/year/month simply passes a new `filter` object in and this hook re-fetches.
 */
const useTransactionData = (filter: TransactionListFilter): UseTransactionDataResult => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [drafts, setDrafts] = useState<ImportDraft[]>([]);
  const [userRole, setUserRole] = useState<string>('user');
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserRole = useCallback(async () => {
    try {
      const response = await fetchWithAuth(apiUrl('auth/me'));
      if (response.ok) {
        const user = await response.json();
        setUserRole(user.role || 'user');
      }
    } catch (err) {
      console.error('Failed to fetch user role:', err);
    }
  }, []);

  const fetchDrafts = useCallback(async () => {
    try {
      // Using simplified endpoint that uses default user internally
      const response = await fetch(apiUrl('import-drafts'), {
        credentials: 'include', // Include cookies for authentication
      });
      if (response.ok) {
        const draftsData = await response.json();
        setDrafts(draftsData);
      }
    } catch (err) {
      console.error('Failed to fetch drafts:', err);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const result = await transactionsApi.getTransactions(filter);
      setTransactions(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError('Failed to fetch transactions. Please try again.');
      console.error('Failed to fetch transactions:', err);
    }
    // `filter` should be a stable (e.g. useMemo'd) reference from the caller, keyed on the
    // actual page/year/month values - see Transactions.tsx - so this only re-fetches when one
    // of those actually changes, not on every unrelated parent re-render.
  }, [filter]);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all([fetchDrafts(), fetchTransactions(), fetchUserRole()]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchDrafts, fetchTransactions, fetchUserRole]);

  // Fetch user's drafts and transactions whenever the filter (page/limit/year/month) changes.
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return {
    transactions,
    drafts,
    userRole,
    isLoading,
    error,
    reload: loadAll,
    setTransactions,
    total,
    totalPages,
  };
};

export default useTransactionData;
